import { Checkbox } from "@msuite/ui/checkbox";
import { useTranslation } from "@msuite/ui/localization";
import { Tooltip } from "@msuite/ui/tooltip";
import type { NotificationChannel } from "@msuite/utils/notifications";
import type { NotificationPreferenceListItem } from "@msuite/utils/types";
import { Controller, useFormContext } from "react-hook-form";
import type { NotificationPreferencesFormValues } from "./notification-preference-form-values";

/** Props Interface */
interface NotificationChannelCellProps {
	item: NotificationPreferenceListItem;
	channel: NotificationChannel;
	isDisabled: boolean;
}

/**
 * One checkbox of the preferences table: whether the kind reaches the person on this channel. A
 * channel the kind is never sent on shows an empty, disabled checkbox with a tooltip saying so.
 * The tooltip hangs on a wrapper, because a disabled control receives no pointer events.
 */
export function NotificationChannelCell({
	item,
	channel,
	isDisabled,
}: NotificationChannelCellProps) {
	/** Context */
	const { t } = useTranslation();
	const { control } = useFormContext<NotificationPreferencesFormValues>();

	/** Guard */
	if (item[channel] === null) {
		return (
			<Tooltip label={t(`notifications.preferences.not_available_${channel}`)}>
				<span className="inline-flex">
					<Checkbox
						value={false}
						onChange={() => {}}
						isDisabled
						data-cy={`notification-preference-${channel}-${item.kind}`}
					/>
				</span>
			</Tooltip>
		);
	}

	/** Render */
	return (
		<Controller
			control={control}
			name={`preferences.${item.kind}.${channel}`}
			render={({ field }) => (
				<Checkbox
					value={field.value === true}
					onChange={field.onChange}
					isDisabled={isDisabled}
					data-cy={`notification-preference-${channel}-${item.kind}`}
				/>
			)}
		/>
	);
}
